import type { ChecklistItem, RecurringOccurrenceState } from '../../types/schema'
import type { ChecklistBookPage } from '../../types/checklistBooks'
import {
  DEFAULT_RECURRING_WINDOW_DAYS,
  formatOccurrenceTitle,
  isoDate,
  occurrenceKeyFor,
  parseIsoDate,
  rollingWindowKeys,
  startOfIsoWeek,
} from '../../services/recurringChecklistService'
import { periodsInWindow } from '../../services/repeat'

export type RecurringOccurrenceMode = 'DAILY' | 'WEEKLY'

export type RecurringOccurrenceEntry = {
  key: string
  label: string
  /** past / current / upcoming = relative to today.
   *  archived                  = date lies past the parent recurringWindow's endDate. */
  kind: 'past' | 'current' | 'upcoming' | 'archived'
  state: RecurringOccurrenceState | null
  meta: string
}

export type RecurringOccurrenceGroupId = 'past' | 'today' | 'upcoming'

export type RecurringOccurrenceGroup = {
  id: RecurringOccurrenceGroupId
  label: string
  entries: RecurringOccurrenceEntry[]
}

/** Cap the drawer's occurrence list. A multi-year parent Goal could otherwise
 *  render thousands of rows; the user can still navigate via the date input. */
export const MAX_OCCURRENCE_ENTRIES = 366

export const getRecurringOccurrenceMode = (
  item: Pick<ChecklistItem, 'checklistType'> | null | undefined,
): RecurringOccurrenceMode | null => {
  if (item?.checklistType === 'DAILY' || item?.checklistType === 'WEEKLY') return item.checklistType
  return null
}

/** Walk pageKeys inclusive of [startIso, endIso] for the given mode. WEEKLY
 *  snaps startIso to that week's Monday and advances one week at a time.
 *  Delegates to periodsInWindow from the client twin (src/services/repeat). */
const enumerateWindowKeys = (
  mode: RecurringOccurrenceMode,
  startIso: string,
  endIso: string,
  cap: number = MAX_OCCURRENCE_ENTRIES,
  timeZone?: string,
): string[] => {
  return periodsInWindow(mode, { startDate: startIso, endDate: endIso }, undefined, timeZone, cap)
}

export const buildVisibleOccurrenceKeys = (
  mode: RecurringOccurrenceMode,
  occurrencePages: readonly ChecklistBookPage[],
  windowDays: number = DEFAULT_RECURRING_WINDOW_DAYS,
  now: Date = new Date(),
  timeZone?: string,
): string[] => {
  const rollingKeys = rollingWindowKeys(mode, windowDays, now, timeZone)
  const firstRollingKey = rollingKeys[0] ?? ''
  const lastRollingKey = rollingKeys[rollingKeys.length - 1] ?? firstRollingKey
  const savedKeys = occurrencePages
    .map((page) => page.pageKey)
    .filter((pageKey) => typeof pageKey === 'string' && pageKey.length > 0)
    .filter((pageKey) => pageKey < firstRollingKey || (pageKey >= firstRollingKey && pageKey <= lastRollingKey))
  return Array.from(new Set([...savedKeys, ...rollingKeys])).sort((left, right) => left.localeCompare(right))
}

export const buildRecurringOccurrenceEntries = (
  item: Pick<ChecklistItem, 'id' | 'title' | 'checklistType' | 'occurrences' | 'recurringWindow'> | null | undefined,
  occurrencePages: readonly ChecklistBookPage[],
  now: Date = new Date(),
  windowDays: number = DEFAULT_RECURRING_WINDOW_DAYS,
  timeZone?: string,
): RecurringOccurrenceEntry[] => {
  const recurringMode = getRecurringOccurrenceMode(item)
  if (!item || !recurringMode) return []

  const todayKey = occurrenceKeyFor(recurringMode, now, timeZone)
  const todayIso = isoDate(now, timeZone)

  // Parent-scoped recurring: prefer the item's own recurringWindow (spans
  // the parent Task/Goal/Subtask range). Fall back to the legacy rolling
  // window for items authored before the refactor.
  const windowStartRaw = item.recurringWindow?.startDate
  const windowEndRaw = item.recurringWindow?.endDate
  let visibleKeys: string[]
  if (windowStartRaw && windowEndRaw) {
    visibleKeys = enumerateWindowKeys(recurringMode, windowStartRaw, windowEndRaw, MAX_OCCURRENCE_ENTRIES, timeZone)
    // Always include today so the "Live" anchor is reachable even when
    // today lies outside the parent window (fully past-dated parent).
    if (!visibleKeys.includes(todayKey) && visibleKeys.length < MAX_OCCURRENCE_ENTRIES) {
      visibleKeys.push(todayKey)
    }
    // Also surface any completed page docs that live outside the window
    // (e.g. after the parent was shrunk) so their history stays reachable.
    for (const page of occurrencePages) {
      if (!page.pageKey) continue
      if (!visibleKeys.includes(page.pageKey) && visibleKeys.length < MAX_OCCURRENCE_ENTRIES) {
        // Only include if the item actually has completion state on that
        // page — no need to surface every workspace page.
        if (page.completions?.[item.id]) visibleKeys.push(page.pageKey)
      }
    }
    visibleKeys.sort((left, right) => left.localeCompare(right))
  } else {
    visibleKeys = buildVisibleOccurrenceKeys(recurringMode, occurrencePages, windowDays, now, timeZone)
  }

  const windowStartIso = windowStartRaw ? windowStartRaw.slice(0, 10) : null
  const windowEndIso = windowEndRaw ? windowEndRaw.slice(0, 10) : null
  const pageByKey = new Map(occurrencePages.map((page) => [page.pageKey, page] as const))
  return visibleKeys.map((key) => {
    const page = pageByKey.get(key)
    const completion = page?.completions?.[item.id]
    const fallbackState = item.occurrences?.[key] ?? null
    const state: RecurringOccurrenceState | null = completion
      ? {
          isCompleted: Boolean(completion.isCompleted),
          workflowStatus: completion.workflowStatus ?? null,
          completedAt: completion.completedAt ?? null,
          completedById: completion.completedById ?? null,
          completedByName: completion.completedByName ?? null,
        }
      : fallbackState
    // Rank the row. TODAY (DAILY) / THIS WEEK (WEEKLY) always wins the
    // Active label — even when the parent window is fully past — so the
    // popup never collapses today's row into "Archived". Rules:
    //   - key == today                                             → current (Active)
    //   - windowFullyPast (parent's whole range is behind today)   → archived
    //   - key is beyond the window on either side (orphan)         → archived
    //   - key < today                                              → past
    //   - key > today                                              → upcoming
    const keyStart = keyStartIsoFor(recurringMode, key)
    const outsideWindow = (windowStartIso && keyStart < windowStartIso)
      || (windowEndIso && keyStart > windowEndIso)
    const windowFullyPast = windowEndIso ? windowEndIso < todayIso : false
    let kind: RecurringOccurrenceEntry['kind']
    if (key === todayKey) {
      kind = 'current'
    } else if (outsideWindow || windowFullyPast) {
      kind = 'archived'
    } else if (key < todayKey) {
      kind = 'past'
    } else {
      kind = 'upcoming'
    }
    return {
      key,
      label: formatOccurrenceTitle(item as ChecklistItem, key),
      kind,
      state,
      meta:
        kind === 'archived'
          ? state?.completedByName
            ? `Completed by ${state.completedByName} · archived`
            : 'Archived · beyond parent range'
          : kind === 'past'
            ? state?.completedByName
              ? `Completed by ${state.completedByName}`
              : state?.workflowStatus
                ? state.workflowStatus.replace(/_/g, ' ').toLowerCase()
                : 'Past occurrence'
            : kind === 'current'
              ? state?.completedAt
                ? `Completed ${new Date(state.completedAt).toLocaleString()}`
                : 'Active occurrence'
              : 'Upcoming occurrence',
    }
  })
}

export const groupRecurringOccurrenceEntries = (
  entries: readonly RecurringOccurrenceEntry[],
): RecurringOccurrenceGroup[] => {
  const past = entries.filter((entry) => entry.kind === 'past' || entry.kind === 'archived')
  const today = entries.filter((entry) => entry.kind === 'current')
  const upcoming = entries.filter((entry) => entry.kind === 'upcoming')
  const groups: RecurringOccurrenceGroup[] = [
    { id: 'past', label: 'Past', entries: past },
    { id: 'today', label: 'Today', entries: today },
    { id: 'upcoming', label: 'Upcoming', entries: upcoming },
  ]
  return groups.filter((group) => group.entries.length > 0)
}

export const occurrenceStatusChipKind = (
  entry: Pick<RecurringOccurrenceEntry, 'kind' | 'state'>,
  isActive: boolean,
): 'active' | 'done' | 'archived' | 'upcoming' | 'past' => {
  if (isActive) return 'active'
  if (entry.state?.isCompleted) return 'done'
  if (entry.kind === 'archived') return 'archived'
  if (entry.kind === 'upcoming') return 'upcoming'
  return 'past'
}

export const occurrenceStatusChipLabel = (
  entry: Pick<RecurringOccurrenceEntry, 'kind' | 'state'>,
  isActive: boolean,
): string => {
  const kind = occurrenceStatusChipKind(entry, isActive)
  if (kind === 'active') return 'Active'
  if (kind === 'done') return 'Done'
  if (kind === 'archived') return 'Archived'
  if (kind === 'upcoming') return 'Upcoming'
  return 'Past'
}

export const formatRecurringOccurrenceRangeSummary = (
  entries: readonly Pick<RecurringOccurrenceEntry, 'key' | 'kind'>[],
  _activeOccurrenceKey?: string | null,
  formatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }),
): string => {
  if (entries.length === 0) return 'No instances'
  const first = entries[0]?.key ?? ''
  const last = entries[entries.length - 1]?.key ?? first
  const formatKey = (key: string) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(key)) return formatter.format(parseIsoDate(key))
    return key
  }
  const activeCount = entries.filter((entry) => entry.kind === 'current').length
  const instanceLabel = entries.length === 1 ? 'instance' : 'instances'
  return `${formatKey(first)} → ${formatKey(last)} · ${entries.length} ${instanceLabel} · ${activeCount} active`
}

/** Given a pageKey, return the earliest ISO date it covers.
 *  DAILY: key itself. WEEKLY: Monday of that ISO week. */
const keyStartIsoFor = (mode: RecurringOccurrenceMode, key: string): string => {
  if (mode === 'DAILY') return key
  const [y, w] = key.split('-W').map(Number)
  if (!y || !w) return key
  const jan4 = new Date(y, 0, 4)
  const week1Monday = startOfIsoWeek(jan4)
  const target = new Date(week1Monday)
  target.setDate(target.getDate() + (w - 1) * 7)
  return isoDate(target)
}
